import { memo } from "react";
import { useAtomValue } from "jotai";
import { ColorPicker, Empty, Button, Tooltip } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { projectMembersWithRolesAtom } from "~/components/Map/Member/MemberStore";
import { getHashColor } from "~/utils/hashStringUtil";
import { LayerStyles, colorToHex } from "../types";

interface CollectorStyleTabProps {
  styles: LayerStyles;
  onUpdateCollectorColor: (userId: number, color: string) => void;
  onResetCollectorColors: () => void;
}

const CollectorStyleTab = ({
  styles,
  onUpdateCollectorColor,
  onResetCollectorColors,
}: CollectorStyleTabProps) => {
  const members = useAtomValue(projectMembersWithRolesAtom);

  const getMemberDefaultColor = (userId: number, username?: string) => {
    return getHashColor(username || userId.toString()).backgroundColor;
  };

  const getMemberCurrentColor = (userId: number, username?: string) => {
    return styles.collectorColors?.[userId] || getMemberDefaultColor(userId, username);
  };

  return (
    <Container>
      <DescriptionBox>
        <span>
          Mỗi nhân viên thu thập sẽ có một màu sắc đặc trưng riêng. Các đối tượng (điểm, đường, vùng) trên bản đồ do nhân viên đó tạo sẽ tự động được tô màu tương ứng.
        </span>
        {members.length > 0 && (
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={onResetCollectorColors}
          >
            Đặt lại màu tự động
          </Button>
        )}
      </DescriptionBox>

      {members.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Chưa có dữ liệu nhân viên trong dự án"
        />
      ) : (
        <MemberList>
          {members.map((item) => {
            const user = item.user;
            if (!user?.id) return null;
            const currentColor = getMemberCurrentColor(user.id, user.username);
            const defaultColor = getMemberDefaultColor(user.id, user.username);
            const isCustom = styles.collectorColors?.[user.id] && styles.collectorColors[user.id] !== defaultColor;

            return (
              <MemberCard key={user.id}>
                <MemberInfo>
                  <ColorBadge style={{ backgroundColor: currentColor }}>
                    {(user.fullname || user.username || "U").charAt(0).toUpperCase()}
                  </ColorBadge>
                  <MemberDetails>
                    <MemberName>{user.fullname || user.username || `User #${user.id}`}</MemberName>
                    <MemberSub>
                      {user.email || user.username || ""}
                      {item.role?.name ? ` • ${item.role.name}` : ""}
                    </MemberSub>
                  </MemberDetails>
                </MemberInfo>

                <ActionsGroup>
                  {isCustom && (
                    <Tooltip title="Khôi phục màu tự động">
                      <Button
                        type="text"
                        size="small"
                        icon={<ReloadOutlined />}
                        onClick={() => onUpdateCollectorColor(user.id, defaultColor)}
                      />
                    </Tooltip>
                  )}
                  <ColorPicker
                    value={currentColor}
                    onChange={(color) => onUpdateCollectorColor(user.id, colorToHex(color))}
                    showText
                  />
                </ActionsGroup>
              </MemberCard>
            );
          })}
        </MemberList>
      )}
    </Container>
  );
};

export default memo(CollectorStyleTab);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 12px 0;
`;

const DescriptionBox = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  background-color: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: #166534;
  line-height: 1.5;
`;

const MemberList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 380px;
  overflow-y: auto;
  padding-right: 4px;
`;

const MemberCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  transition: all 0.2s;

  &:hover {
    border-color: #cbd5e1;
    background: #f8fafc;
  }
`;

const MemberInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ColorBadge = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-weight: 600;
  font-size: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
`;

const MemberDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const MemberName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
`;

const MemberSub = styled.span`
  font-size: 12px;
  color: #64748b;
`;

const ActionsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
